// SmartThemeProvider — يربط مُنتقي هويّة العلامة (smartstable/nudrah) بمتغيّرات CSS الدلاليّة
// (shadcn/ui + Tailwind)، ويحفظ الاختيار في نفس مفتاح localStorage السابق (smart_theme_palette).
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { Toaster } from 'sonner';
import { DEFAULT_PALETTE_KEY, THEME_PALETTES, type BrandTokens } from './brandPalettes';
import { applyShadcnVars } from './shadcnVars';
import { readStorage, writeStorage, THEME_PALETTE_KEY } from '../lib/storage';

interface SmartThemeContextValue {
	paletteKey: string;
	tokens: BrandTokens;
	setPaletteKey: (key: string) => void;
	availablePalettes: typeof THEME_PALETTES;
}

const SmartThemeContext = createContext<SmartThemeContextValue | undefined>(undefined);

function getStoredPaletteKey(): string {
	const stored = readStorage(THEME_PALETTE_KEY);
	return stored && THEME_PALETTES[stored] ? stored : DEFAULT_PALETTE_KEY;
}

export function SmartThemeProvider({ children }: { children: ReactNode }) {
	const [paletteKey, setPaletteKeyState] = useState<string>(getStoredPaletteKey);

	const tokens = THEME_PALETTES[paletteKey]?.colors ?? THEME_PALETTES[DEFAULT_PALETTE_KEY].colors;

	const setPaletteKey = useCallback((key: string) => {
		if (!THEME_PALETTES[key]) return;
		writeStorage(THEME_PALETTE_KEY, key);
		setPaletteKeyState(key);
	}, []);

	// نُطبِّق متغيّرات CSS فورًا (أثناء الرسم) لا بعده — يمنع «وميض» ألوان خاطئة.
	applyShadcnVars(tokens);

	const contextValue = useMemo(
		() => ({ paletteKey, tokens, setPaletteKey, availablePalettes: THEME_PALETTES }),
		[paletteKey, tokens, setPaletteKey],
	);

	return (
		<SmartThemeContext.Provider value={contextValue}>
			{children}
			<Toaster position="top-center" richColors closeButton />
		</SmartThemeContext.Provider>
	);
}

export function useSmartTheme(): SmartThemeContextValue {
	const ctx = useContext(SmartThemeContext);
	if (!ctx) throw new Error('useSmartTheme must be used within SmartThemeProvider');
	return ctx;
}
